import React, { useState, useEffect, useRef } from "react";

const GaugeComponent = ({
  value = 0,
  minValue = 0,
  maxValue = 100,
  labels = {},
  pointer = { color: "#22d3ee" },
  ticks = [],
  accentColor = "#22d3ee"
}) => {
  const clamped = Math.min(Math.max(value, minValue), maxValue);
  const pct = ((clamped - minValue) / (maxValue - minValue)) * 100;

  // Smooth angle interpolation
  const targetAngle = (pct / 100) * 180 - 90; // -90° to +90°
  const angleRef = useRef(targetAngle);
  const [displayAngle, setDisplayAngle] = useState(targetAngle);
  const rafRef = useRef(null);

  useEffect(() => {
    const animate = () => {
      const current = angleRef.current;
      const next = current + (targetAngle - current) * 0.15; // damping
      angleRef.current = Math.abs(next - targetAngle) < 0.2 ? targetAngle : next;
      setDisplayAngle(angleRef.current);
      if (angleRef.current !== targetAngle) rafRef.current = requestAnimationFrame(animate);
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [targetAngle]);

  // Arc length approximation for strokeDasharray (semi-circle path length ≈ 251)
  const dash = Math.max(0, Math.min(251, pct * 2.51));
  const valueText =
    labels?.valueLabel?.formatTextValue
      ? labels.valueLabel.formatTextValue(clamped)
      : clamped.toFixed(1);

  // Enhanced color scheme with smooth transitions
  const progressColor = pct > 80 
    ? "url(#dangerGradient)" 
    : pct > 50 
      ? "url(#warningGradient)" 
      : "url(#safeGradient)";

  // Convert a scalar value to [x, y] on the semi-circle for tick marks
  const polarToCartesian = (val) => {
    const p = ((val - minValue) / (maxValue - minValue)) * Math.PI; // 0..π
    const cx = 100, cy = 100, r = 80;
    const x = cx + r * Math.cos(Math.PI - p);
    const y = cy - r * Math.sin(Math.PI - p);
    return { x, y };
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg viewBox="0 0 200 140" className="w-full h-full">
        {/* Gradient Definitions */}
        <defs>
          {/* Progress gradients */}
          <linearGradient id="safeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
          <linearGradient id="warningGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
          <linearGradient id="dangerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#dc2626" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
          
          {/* Background gradient */}
          <linearGradient id="backgroundGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(148, 163, 184, 0.1)" />
            <stop offset="50%" stopColor="rgba(148, 163, 184, 0.2)" />
            <stop offset="100%" stopColor="rgba(148, 163, 184, 0.1)" />
          </linearGradient>

          {/* Glow effects */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <filter id="pointerGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer ring shadow */}
        <path
          d="M 16 100 A 84 84 0 0 1 184 100"
          fill="none"
          stroke="rgba(0,0,0,0.3)"
          strokeWidth="8"
          strokeLinecap="round"
        />

        {/* Background arc with gradient */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="url(#backgroundGradient)"
          strokeWidth="12"
          strokeLinecap="round"
        />

        {/* Progress arc with glow */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={progressColor}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${dash} 251`}
          className="transition-all duration-700 ease-out"
          filter="url(#glow)"
        />

        {/* Inner decorative ring */}
        <path
          d="M 30 100 A 70 70 0 0 1 170 100"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1"
          strokeLinecap="round"
        />

        {/* Enhanced tick marks */}
        {ticks?.map((t, i) => {
          const { x, y } = polarToCartesian(t);
          const v = (t - minValue) / (maxValue - minValue);
          const angle = Math.PI * v;
          const r1 = 80, r2 = 68;
          const cx = 100, cy = 100;
          const x1 = cx + r1 * Math.cos(Math.PI - angle);
          const y1 = cy - r1 * Math.sin(Math.PI - angle);
          const x2 = cx + r2 * Math.cos(Math.PI - angle);
          const y2 = cy - r2 * Math.sin(Math.PI - angle);
          
          return (
            <g key={i}>
              {/* Tick mark with gradient */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(255,255,255,0.7)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              {/* Tick label with better positioning */}
              <text
                x={x}
                y={y}
                dy="-8"
                textAnchor="middle"
                className="fill-slate-300"
                style={{ 
                  fontSize: 11, 
                  fontWeight: 600,
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))"
                }}
              >
                {labels?.tickLabels?.valueConfig?.formatTextValue
                  ? labels.tickLabels.valueConfig.formatTextValue(t)
                  : t}
              </text>
            </g>
          );
        })}

        {/* Center hub with gradient */}
        <circle 
          cx="100" 
          cy="100" 
          r="8" 
          fill="url(#backgroundGradient)" 
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="2"
        />
        <circle cx="100" cy="100" r="4" fill="rgba(255,255,255,0.9)" />

        {/* Pointer with enhanced styling */}
        <g transform={`rotate(${displayAngle} 100 100)`}>
          {/* Pointer shadow */}
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="38"
            stroke="rgba(0,0,0,0.3)"
            strokeWidth="4"
            strokeLinecap="round"
            transform="translate(1,1)"
          />
          {/* Main pointer */}
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="40"
            stroke={pointer?.color || accentColor}
            strokeWidth="3.5"
            strokeLinecap="round"
            filter="url(#pointerGlow)"
          />
          {/* Pointer highlight */}
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="42"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>

        {/* Value display with enhanced styling */}
        <g>
          {/* Value background */}
          <rect
            x="60"
            y="120"
            width="80"
            height="20"
            rx="10"
            fill="rgba(0,0,0,0.4)"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1"
          />
          {/* Value text */}
          <text
            x="100"
            y="134"
            textAnchor="middle"
            className="fill-white"
            style={{ 
              fontSize: 16, 
              fontWeight: 700,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))"
            }}
          >
            {valueText}
          </text>
        </g>

        {/* Decorative elements */}
        <circle cx="40" cy="100" r="2" fill="rgba(255,255,255,0.2)" />
        <circle cx="160" cy="100" r="2" fill="rgba(255,255,255,0.2)" />
      </svg>
    </div>
  );
};

/** Enhanced Gauge Variants with decimal formatting */
const GaugeChartVoltage = ({ metric, isConnected, lastUpdate }) => (
  <div className={`w-full h-64 transition-all duration-500 ${!isConnected ? "opacity-40 grayscale" : ""}`}>
    <GaugeComponent
      value={metric}
      minValue={0}
      maxValue={260}
      labels={{
        valueLabel: { formatTextValue: (v) => `${v.toFixed(1)} V` },
        tickLabels: {
          valueConfig: { formatTextValue: (v) => `${v}V` },
        },
      }}
      pointer={{ color: "#06b6d4" }}
      accentColor="#06b6d4"
      ticks={[50, 100, 150, 200]}
    />
    <div className="text-center mt-6">
      <div className="text-sm text-slate-400 font-semibold tracking-wide uppercase">Operating Range</div>
      <div className="text-ls text-slate-300 font-medium mt-1">0–260V</div>
      {lastUpdate && (
        <div className="text-xs text-slate-500 mt-2">
          Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}
    </div>
  </div>
);

const GaugeChartCurrent = ({ metric, isConnected, lastUpdate }) => (
  <div className={`w-full h-64 transition-all duration-500 ${!isConnected ? "opacity-40 grayscale" : ""}`}>
    <GaugeComponent
      value={metric}
      minValue={0}
      maxValue={120}
      labels={{
        valueLabel: { formatTextValue: (v) => `${v.toFixed(1)} A` },
        tickLabels: {
          valueConfig: { formatTextValue: (v) => `${v}A` },
        },
      }}
      pointer={{ color: "#3b82f6" }}
      accentColor="#3b82f6"
      ticks={[25, 50, 75, 100]}
    />
    <div className="text-center mt-6">
      <div className="text-sm text-slate-400 font-semibold tracking-wide uppercase">Operating Range</div>
      <div className="text-ls text-slate-300 font-medium mt-1">0–120A</div>
      {lastUpdate && (
        <div className="text-xs text-slate-500 mt-2">
          Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}
    </div>
  </div>
);

const GaugeChartPower = ({ metric, isConnected, lastUpdate }) => (
  <div className={`w-full h-64 transition-all duration-500 ${!isConnected ? "opacity-40 grayscale" : ""}`}>
    <GaugeComponent
      value={metric}
      minValue={0}
      maxValue={3000}
      labels={{
        valueLabel: { formatTextValue: (v) => `${v.toFixed(1)} W` },
        tickLabels: {
          valueConfig: { formatTextValue: (v) => `${v}W` },
        },
      }}
      pointer={{ color: "#10b981" }}
      accentColor="#10b981"
      ticks={[600, 1200, 1800, 2400]}
    />
    <div className="text-center mt-6">
      <div className="text-sm text-slate-400 font-semibold tracking-wide uppercase">Operating Range</div>
      <div className="text-ls text-slate-300 font-medium mt-1">0–3000W</div>
      {lastUpdate && (
        <div className="text-xs text-slate-500 mt-2">
          Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}
    </div>
  </div>
);

const GaugeChartFrequency = ({ metric, isConnected, lastUpdate }) => (
  <div className={`w-full h-64 transition-all duration-500 ${!isConnected ? "opacity-40 grayscale" : ""}`}>
    <GaugeComponent
      value={metric}
      minValue={1}
      maxValue={99}
      labels={{
        valueLabel: { formatTextValue: (v) => `${v.toFixed(1)} Hz` },
        tickLabels: {
          valueConfig: { formatTextValue: (v) => `${v}Hz` },
        },
      }}
      pointer={{ color: "#e879f9" }}
      accentColor="#e879f9"
      ticks={[20, 40, 60, 80]}
    />
    <div className="text-center mt-6">
      <div className="text-sm text-slate-400 font-semibold tracking-wide uppercase">Operating Range</div>
      <div className="text-ls text-slate-300 font-medium mt-1">1–99Hz</div>
      {lastUpdate && (
        <div className="text-xs text-slate-500 mt-2">
          Updated: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}
    </div>
  </div>
);

/**
 * MAIN: Enhanced Cards with improved layout and visual design
 * Now compatible with 1-minute data storage backend
 */
const Cards = () => {
  // Real-time metric states with proper initialization
  const [gaugeValueV, setGaugeValueV] = useState(0);
  const [gaugeValueA, setGaugeValueA] = useState(0);
  const [gaugeValueW, setGaugeValueW] = useState(0);
  const [gaugeValueHz, setGaugeValueHz] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [dataStats, setDataStats] = useState({
    receivedCount: 0,
    storageInterval: '1 minute'
  });

  // Refs for socket & retry timer
  const socketRef = useRef(null);
  const retryRef = useRef(null);
  const dataCountRef = useRef(0);

  useEffect(() => {
    let unmounted = false;

    const connect = () => {
      const url = process.env.REACT_APP_ENDPOINT_WS || "ws://localhost:5000";
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        setDataStats(prev => ({ ...prev, receivedCount: 0 }));
        dataCountRef.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("Received WebSocket message:", message);
          
          // Handle connection message
          if (message.type === 'connection') {
            console.log(`Server info: Storage interval = ${message.interval}`);
            setDataStats(prev => ({ 
              ...prev, 
              storageInterval: message.interval || '1 minute' 
            }));
            return;
          }
          
          // Handle aggregated data notification (optional)
          if (message.type === 'aggregated' && message.dataType === 'power') {
            console.log('Server stored aggregated data:', message.data);
            // You could update a notification or log here
            return;
          }
          
          // Handle real-time data (this is what updates the gauges)
          if (message.type === 'realtime') {
            const newData = message;
            const now = new Date();
            setLastUpdate(now);
            dataCountRef.current += 1;
            
            // Update gauge values with real-time data
            if (typeof newData.voltage === "number" && !isNaN(newData.voltage)) {
              setGaugeValueV(newData.voltage);
            }
            if (typeof newData.current === "number" && !isNaN(newData.current)) {
              setGaugeValueA(newData.current);
            }
            if (typeof newData.power === "number" && !isNaN(newData.power)) {
              setGaugeValueW(newData.power);
            }
            if (typeof newData.frequency === "number" && !isNaN(newData.frequency)) {
              setGaugeValueHz(newData.frequency);
            }
            
            // Update stats every 10 data points
            if (dataCountRef.current % 10 === 0) {
              setDataStats(prev => ({ 
                ...prev, 
                receivedCount: dataCountRef.current 
              }));
            }
          } 
          // Handle legacy format (for backward compatibility)
          else if (!message.type && typeof message.voltage === "number") {
            console.log("Received legacy format data");
            const now = new Date();
            setLastUpdate(now);
            dataCountRef.current += 1;
            
            if (typeof message.voltage === "number" && !isNaN(message.voltage)) {
              setGaugeValueV(message.voltage);
            }
            if (typeof message.current === "number" && !isNaN(message.current)) {
              setGaugeValueA(message.current);
            }
            if (typeof message.power === "number" && !isNaN(message.power)) {
              setGaugeValueW(message.power);
            }
            if (typeof message.frequency === "number" && !isNaN(message.frequency)) {
              setGaugeValueHz(message.frequency);
            }
          }
        } catch (e) {
          console.error("Invalid WebSocket message format:", e);
          console.error("Raw message:", event.data);
        }
      };

      socket.onclose = (event) => {
        console.log(`WebSocket disconnected: ${event.code} ${event.reason}`);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        if (!unmounted) {
          // Exponential backoff for reconnection
          const delay = Math.min(30000, 5000 * Math.pow(1.5, dataCountRef.current % 10));
          console.log(`Attempting reconnect in ${delay/1000} seconds...`);
          retryRef.current = setTimeout(connect, delay);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        setConnectionStatus('error');
        try {
          socket.close();
        } catch {}
      };
    };

    connect();

    // Cleanup function
    return () => {
      unmounted = true;
      if (retryRef.current) clearTimeout(retryRef.current);
      if (socketRef.current && socketRef.current.readyState <= 1) {
        try {
          socketRef.current.close();
        } catch {}
      }
    };
  }, []);

  // Format connection status with colors
  const getStatusDisplay = () => {
    switch(connectionStatus) {
      case 'connected':
        return { text: 'Connected', color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20' };
      case 'connecting':
        return { text: 'Connecting...', color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20' };
      case 'error':
        return { text: 'Connection Error', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20' };
      default:
        return { text: 'Disconnected', color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/20' };
    }
  };

  const status = getStatusDisplay();

  return (
    <div className="bg-gradient-to-br from-slate-900/90 via-slate-900/90 to-slate-900/90 p-8 relative overflow-hidden rounded-2xl pb-56 md:pb-32">
      {/* Enhanced background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/3 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-purple-500/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-3/4 left-1/2 w-[400px] h-[400px] bg-green-500/2 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
      </div>
      
      <div className="max-w-8xl mx-auto relative">
        {/* Enhanced Header with connection status */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-4 mb-6">           
            <div className="text-left">
              <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent leading-tight">
                Power Monitoring Dashboard
              </h1>
            </div>
          </div>
          <p className="text-slate-400 text-xl font-semibold tracking-wide mb-4">
            Real-time electrical system monitoring and analytics
          </p>
          

        </div>

        {/* Enhanced gauge cards grid */}
        <section className="grid gap-6 lg:gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {/* Voltage Card */}
          <div className="group relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500/15 via-blue-500/10 to-cyan-500/15 rounded-3xl blur-xl transition-all duration-700 group-hover:blur-2xl group-hover:from-cyan-500/25 group-hover:to-blue-500/25 opacity-0 group-hover:opacity-100" />
            <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-750/90 to-slate-800/95 backdrop-blur-sm rounded-3xl p-6 border border-slate-600/40 shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-cyan-500/20 hover:border-slate-500/60">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 via-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <span className="text-white font-black text-lg">V</span>
                </div>
                <div>
                  <h3 className="text-white text-2xl font-bold tracking-tight">Voltage</h3>
                  <p className="text-slate-400 text-sm font-medium">System voltage monitoring</p>
                </div>
              </div>
              <div className="flex justify-center">
                <GaugeChartVoltage metric={gaugeValueV} isConnected={isConnected} lastUpdate={lastUpdate} />
              </div>
            </div>
          </div>

          {/* Current Card */}
          <div className="group relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/15 via-indigo-500/10 to-blue-500/15 rounded-3xl blur-xl transition-all duration-700 group-hover:blur-2xl group-hover:from-blue-500/25 group-hover:to-indigo-500/25 opacity-0 group-hover:opacity-100" />
            <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-750/90 to-slate-800/95 backdrop-blur-sm rounded-3xl p-6 border border-slate-600/40 shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-blue-500/20 hover:border-slate-500/60">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <span className="text-white font-black text-lg">A</span>
                </div>
                <div>
                  <h3 className="text-white text-2xl font-bold tracking-tight">Current</h3>
                  <p className="text-slate-400 text-sm font-medium">Current flow measurement</p>
                </div>
              </div>
              <div className="flex justify-center">
                <GaugeChartCurrent metric={gaugeValueA} isConnected={isConnected} lastUpdate={lastUpdate} />
              </div>
            </div>
          </div>

          {/* Power Card */}
          <div className="group relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-green-500/15 rounded-3xl blur-xl transition-all duration-700 group-hover:blur-2xl group-hover:from-green-500/25 group-hover:to-emerald-500/25 opacity-0 group-hover:opacity-100" />
            <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-750/90 to-slate-800/95 backdrop-blur-sm rounded-3xl p-6 border border-slate-600/40 shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-green-500/20 hover:border-slate-500/60">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 via-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                  <span className="text-white font-black text-lg">W</span>
                </div>
                <div>
                  <h3 className="text-white text-2xl font-bold tracking-tight">Power</h3>
                  <p className="text-slate-400 text-sm font-medium">Total power consumption</p>
                </div>
              </div>
              <div className="flex justify-center">
                <GaugeChartPower metric={gaugeValueW} isConnected={isConnected} lastUpdate={lastUpdate} />
              </div>
            </div>
          </div>

          {/* Frequency Card */}
          <div className="group relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500/15 via-pink-500/10 to-purple-500/15 rounded-3xl blur-xl transition-all duration-700 group-hover:blur-2xl group-hover:from-purple-500/25 group-hover:to-pink-500/25 opacity-0 group-hover:opacity-100" />
            <div className="relative bg-gradient-to-br from-slate-800/95 via-slate-750/90 to-slate-800/95 backdrop-blur-sm rounded-3xl p-6 border border-slate-600/40 shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-purple-500/20 hover:border-slate-500/60">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 via-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <span className="text-white font-black text-lg">Hz</span>
                </div>
                <div>
                  <h3 className="text-white text-2xl font-bold tracking-tight">Frequency</h3>
                  <p className="text-slate-400 text-sm font-medium">AC frequency monitoring</p>
                </div>
              </div>
              <div className="flex justify-center">
                <GaugeChartFrequency metric={gaugeValueHz} isConnected={isConnected} lastUpdate={lastUpdate} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Cards;